/**
 * Stage Wrapper Component
 *
 * Automatically wraps campaign stage components with back/next functionality.
 * No need to handle navigation in individual components!
 */

import React from 'react'
import { ArrowLeft, ArrowRight, Loader2 } from 'lucide-react'

export interface StageWrapperProps {
  children: React.ReactNode
  currentStepIndex: number
  totalSteps: number
  handleNavigate: (targetIndex: number) => void | Promise<void>
  handleNavigateWithCompletion?: (targetIndex: number) => Promise<void>
  onNext?: () => Promise<boolean> | boolean  // Return false to prevent navigation
  onBack?: () => void | Promise<void>
  showBackButton?: boolean
  showNextButton?: boolean
  nextButtonText?: string
  backButtonText?: string
  isNavigating?: boolean
  isCompleting?: boolean
  customButtons?: React.ReactNode  // For stages that need custom buttons (like Submit)
  useCompletionOnNext?: boolean  // Use handleNavigateWithCompletion for next button
  onComplete?: () => Promise<void> | void // Callback for final step completion
}

export function StageWrapper({
  children,
  currentStepIndex,
  totalSteps,
  handleNavigate,
  handleNavigateWithCompletion,
  onNext,
  onBack,
  showBackButton = true,
  showNextButton = true,
  nextButtonText = 'Continue',
  backButtonText = 'Back',
  isNavigating = false,
  isCompleting = false,
  customButtons,
  useCompletionOnNext = true,
  onComplete
}: StageWrapperProps) {
  const [isProcessing, setIsProcessing] = React.useState(false)

  const isFinalStep = currentStepIndex === totalSteps - 1
  const canGoNext = currentStepIndex < totalSteps - 1 || (isFinalStep && nextButtonText !== 'Continue')
  const canGoBack = currentStepIndex > 0

  const handleNextClick = async () => {
    if (isProcessing || isNavigating || isCompleting) return

    setIsProcessing(true)
    try {
      // If onNext is provided, call it first (for validation)
      if (onNext) {
        const canProceed = await onNext()
        if (canProceed === false) {
          setIsProcessing(false)
          return
        }
      }

      // Navigate to next step
      const nextStepIndex = currentStepIndex + 1

      if (isFinalStep && onComplete) {
        await onComplete()
      } else if (useCompletionOnNext && handleNavigateWithCompletion) {
        await handleNavigateWithCompletion(nextStepIndex)
      } else {
        await handleNavigate(nextStepIndex)
      }
    } catch (error) {
      console.error('Navigation error:', error)
    } finally {
      setIsProcessing(false)
    }
  }

  const handleBackClick = async () => {
    if (isProcessing || isNavigating) return

    setIsProcessing(true)
    try {
      // If onBack is provided, call it first
      if (onBack) {
        await onBack()
      }

      // Navigate to previous step
      const prevStepIndex = currentStepIndex - 1
      await handleNavigate(prevStepIndex)

      // Scroll to top after navigation
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }, 100)
    } catch (error) {
      console.error('Back navigation error:', error)
    } finally {
      setIsProcessing(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* Content */}
      <div className="flex-1">
        {children}
      </div>

      {/* Navigation Buttons */}
      {customButtons ? (
        customButtons
      ) : (
        <div className="flex justify-between">
          {/* Back Button */}
          {showBackButton && (
            <button
              onClick={handleBackClick}
              disabled={isNavigating || isProcessing || !canGoBack}
              className="btn-outline flex items-center space-x-2 min-w-[100px] justify-center"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>{backButtonText}</span>
            </button>
          )}

          {!showBackButton && <div />}

          {/* Next Button */}
          {showNextButton && canGoNext && (
            <button
              onClick={handleNextClick}
              disabled={isNavigating || isCompleting || isProcessing}
              className="btn-primary flex items-center space-x-2 min-w-[120px] justify-center"
            >
              {isNavigating || isCompleting || isProcessing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <span>{nextButtonText}</span>
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
